import React, { useEffect, useState, useRef, useCallback } from "react";
import { X, ZoomIn, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

export const Gallery = () => {
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Tracks active modal media item by index (null = closed)
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Observer reference for infinite scrolling trigger
  const observer = useRef();

  const fetchGalleryData = async (page) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await fetch(
        `https://webadmin.naturx.co.in/api/getgallery?type=0&page=${page}`,
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();

      if (data.status === 1) {
        const newItems = data.data.data;
        const lastPage = data.data.last_page;

        setGalleryData((prev) =>
          page === 1 ? newItems : [...prev, ...newItems],
        );
        setHasMore(page < lastPage);
      } else {
        throw new Error("Failed to fetch gallery data");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchGalleryData(currentPage);
  }, [currentPage]);

  // Infinite Scroll Trigger
  const lastElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore],
  );

  // Transform raw data into structured gallery items
  const galleryItems =
    galleryData?.map((item, index) => ({
      id: item.id || index,
      imageUrl: item.image,
      type: item.type,
      title: `Elegant Surface ${index + 1}`,
      category: "Premium Texture",
    })) || [];

  // Lightbox Modal Navigation
  const closeModal = () => setSelectedIndex(null);

  const nextSlide = () => {
    setSelectedIndex((prev) =>
      prev === galleryItems.length - 1 ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setSelectedIndex((prev) =>
      prev === 0 ? galleryItems.length - 1 : prev - 1,
    );
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, galleryItems.length]);

  if (loading) {
    return (
      <div className="py-20 text-center text-gray-500 flex flex-col items-center justify-center gap-2">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        <span>Loading gallery items...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center text-red-500">
        Error loading gallery: {error}
      </div>
    );
  }

  return (
    <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Page Title & Subtitle */}
      <div className="text-center max-w-3xl mx-auto mb-10">
        <h1 className="text-xl lg:text-3xl font-semibold text-gray-900 leading-tight">
          Our{" "}
          <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-transparent bg-clip-text">
            Gallery
          </span>
        </h1>

        <div className="w-24 h-1 bg-orange-500 mx-auto my-4 rounded-full"></div>

        <p className="text-gray-600 text-md">
          Exclusive gallery showcasing premium textures for elegant, timeless
          designs.
        </p>
      </div>

      {/* Media Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {galleryItems.map((item, index) => {
          const isLastItem = index === galleryItems.length - 1;
          return (
            <div
              key={item.id}
              ref={isLastItem ? lastElementRef : null}
              onClick={() => setSelectedIndex(index)}
              className="relative rounded-xl overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="relative h-[550px] w-full overflow-hidden">
                {item.type === 1 ? (
                  <img
                    src={item.imageUrl}
                    alt={`Gallery item ${index + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : item.type === 2 ? (
                  <video
                    src={item.imageUrl}
                    muted
                    autoPlay
                    loop
                    playsInline
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : null}

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Zoom Indicator */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/20 backdrop-blur-md p-2 rounded-full text-white">
                    <ZoomIn className="w-5 h-5" />
                  </div>
                </div>

                {/* Overlay Content */}
                <div className="absolute bottom-5 left-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <p className="text-sm text-orange-300 font-medium">
                    {item.category}
                  </p>
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Loading Indicator */}
      {loadingMore && (
        <div className="py-8 text-center text-gray-500 flex justify-center items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          <span>Loading more items...</span>
        </div>
      )}

      {/* Fullscreen Lightbox Modal */}
      {selectedIndex !== null && galleryItems[selectedIndex] && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 cursor-pointer"
          onClick={closeModal} // Clicking anywhere inside this container will close the modal
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-6 right-6 z-50 bg-white/10 hover:bg-white/20 backdrop-blur-md p-2.5 rounded-full text-white transition-colors"
            aria-label="Close Modal"
          >
            <X size={24} />
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Stops click from closing modal when navigating
              prevSlide();
            }}
            className="absolute left-4 md:left-8 z-50 bg-white/10 hover:bg-white/20 backdrop-blur-md p-2.5 rounded-full text-white transition-colors"
            aria-label="Previous Media"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Active Media Container - Removed e.stopPropagation() so clicking image also closes popup */}
          <div className="w-full max-w-6xl flex items-center justify-center relative">
            {galleryItems[selectedIndex].type === 1 ? (
              <img
                key={selectedIndex}
                src={galleryItems[selectedIndex].imageUrl}
                alt={galleryItems[selectedIndex].title}
                className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
              />
            ) : (
              <video
                key={selectedIndex}
                src={galleryItems[selectedIndex].imageUrl}
                controls
                autoPlay
                onClick={(e) => e.stopPropagation()} // Prevents video play/pause controls from triggering close
                className="max-w-full max-h-[85vh] rounded-xl shadow-2xl"
              />
            )}
          </div>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Stops click from closing modal when navigating
              nextSlide();
            }}
            className="absolute right-4 md:right-8 z-50 bg-white/10 hover:bg-white/20 backdrop-blur-md p-2.5 rounded-full text-white transition-colors"
            aria-label="Next Media"
          >
            <ChevronRight size={24} />
          </button>

          {/* Item Counter Indicator */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-medium">
            {selectedIndex + 1} / {galleryItems.length}
          </div>
        </div>
      )}
    </div>
  );
};
